import React, { useContext, useEffect, useRef, useState } from "react";
import ViewerContext from "../../context/ViewerContext";
import { CzmlDataSource, EventHelper } from "cesium";
import { igcToCzml } from "../../helper/czmlHelper";
import {
  findColoredDataSource,
  zoomToEntityBound,
} from "../../helper/cesiumHelper";
import { getVarioTrack } from "../../helper/varioTrack";
import {
  isFlightHidden,
  pruneFlightVisibility,
} from "../../helper/flightVisibility";
import { applySolo, pruneSolo } from "../../helper/soloMode";
import { calibrateRenderQuality } from "../../helper/renderQuality";

let currentTimeState;
let igcParsedDataCount = 0;
const CreateDataSource = () => {
  const { igcParsedData, coloredFile, trailDuration } = useContext(ViewerContext);

  useEffect(() => {
    dataSourceEventHandling();
  }, []);

  useEffect(() => {
    currentTimeState = viewer.clock.currentTime.clone();
    if (coloredFile) {
      addIGCDataOnMapWithColor(coloredFile);
    } else {
      switchColorDataSource(true);
    }
  }, [coloredFile]);

  useEffect(() => {
    currentTimeState = null;
    if (igcParsedData.length > 0) {
      igcParsedDataCount = igcParsedData.length;
      pruneFlightVisibility(igcParsedData.map((f) => f.id));
      pruneSolo(viewer, igcParsedData.map((f) => f.id));
      addIGCDataOnMap(igcParsedData);
    } else {
      igcParsedDataCount = 0;
      pruneFlightVisibility([]);
      pruneSolo(viewer, []);
      getVarioTrack(viewer).clear();
      console.log("clear");
    }
  }, [igcParsedData]);

  const addIGCDataOnMapWithColor = (fileId) => {
    switchColorDataSource();

    //hide original data source
    let dataSource = viewer.dataSources.getByName(fileId);
    if (dataSource.length == 1) {
      dataSource[0].show = false;
    }
    //Add new colored data source
    const dataIndex = igcParsedData.findIndex((e) => e.id == fileId);
    if (dataIndex != -1) {
      const flightItem = igcParsedData[dataIndex];
      let obj = {
        data: flightItem.data,
        name: flightItem.pilotName,
        id: flightItem.id,
        colored: true,
        color: flightItem.color,
        zoom: false,
      };
      createCZMLDataSource(obj);
    }
  };

  const addIGCDataOnMap = (dataList) => {
    viewer.dataSources.removeAll();
    getVarioTrack(viewer).clear();
    let colorMode = false;
    if (dataList.length == 1) {
      colorMode = true;
    }

    dataList.forEach((flightItem) => {
      let obj = {
        data: flightItem.data,
        name: flightItem.pilotName,
        id: flightItem.id,
        colored: colorMode,
        color: flightItem.color,
        zoom: true,
      };
      createCZMLDataSource(obj);
    });

    // Now that there is a real track on screen, check this device can actually
    // afford the render resolution we asked for. Runs at most once per session.
    calibrateRenderQuality(viewer);
  };

  const createCZMLDataSource = ({
    data,
    id,
    name,
    colored = false,
    zoom = true,
    color,
  }) => {
    let czmlData;
    // Enable performance mode for large scenes (reduces CZML overhead by 90%)
    const performanceMode = igcParsedData.length > 15;
    const obj = {
      flightData: data,
      id: id,
      pilotName: name,
      colored: colored,
      color: color,
      trailDuration: trailDuration,
      performanceMode: performanceMode,
    };
    czmlData = igcToCzml(obj);

    // Pause the clock before loading the CZML file
    // viewer.clock.shouldAnimate = false;

    // A flight the user unchecked must stay hidden across re-colouring — the
    // data source it was hidden on may well have been destroyed since
    // (helper/flightVisibility.js). Set `show` BEFORE adding it to the
    // collection, so the dataSourceAdded listeners below already see it and
    // don't hand the camera to a hidden pilot.
    const dataSourcePromise = CzmlDataSource.load(czmlData).then((ds) => {
      ds.show = !isFlightHidden(id);
      return ds;
    });
    // …and once it is in the scene, repaint it for solo mode: its entities are
    // brand new, so they carry none of the dimming the others already have.
    dataSourcePromise.then(() => applySolo(viewer));
    viewer.dataSources.add(dataSourcePromise);
    // if (zoom)
    //     viewer.zoomTo(dataSourcePromise);
    viewer.clock.shouldAnimate = true;

    // Colored ("Vario Track") flight → draw its track as one efficient
    // PolylineCollection primitive that reveals with the clock.
    if (colored) {
      const varioTrack = getVarioTrack(viewer);
      varioTrack.show(id, data);
      varioTrack.setVisible(!isFlightHidden(id));
    }

    // CzmlDataSource.load(czmlData).then(function (dataSource) {

    //     viewer.dataSources.add(dataSource);
    //     // viewer.timeline.updateFromClock();
    //     viewer.clock.shouldAnimate = true;
    //     if (zoom) {
    //         viewer.zoomTo(dataSource)
    //     }
    // })
  };

  const switchColorDataSource = (trackEntity = false) => {
    getVarioTrack(viewer).clear(); // drop the old vario primitive before re/un-colouring
    const currentColorDatasource = findColoredDataSource();
    if (currentColorDatasource) {
      //remove colored one
      viewer.dataSources.remove(currentColorDatasource);

      //show original data source if exist — but only if the user hasn't
      //unchecked this flight. Forcing `true` here is what made a hidden pilot's
      //track reappear after colouring a different pilot.
      const name = currentColorDatasource.name.split("_colored")[0];
      let dataSource = viewer.dataSources.getByName(name);
      if (dataSource.length == 1) {
        const visible = !isFlightHidden(name);
        dataSource[0].show = visible;
        applySolo(viewer); // it was the solo/coloured flight; it may need dimming now
        if (trackEntity && visible) {
          let entities = dataSource[0].entities.values;
          if (entities.length > 0) {
            const entity = entities[0];
            viewer.trackedEntity = entity;
          }
        }
      }
    }
  };

  const dataSourceEventHandling = () => {
    const cesiumEventHelper = new EventHelper();

    const cesiumDataSourceRemoveListener = (dataSource) => {
      if (currentTimeState) {
        viewer.clock.currentTime = currentTimeState;
        console.log("removed");
      }
    };

    const cesiumDataSourceAddListener = (dataSource) => {
      if (currentTimeState) {
        viewer.clock.currentTime = currentTimeState;
        const currentColorDatasource = findColoredDataSource();
        if (currentColorDatasource && currentColorDatasource.show) {
          let entities = currentColorDatasource.entities.values;
          if (entities.length > 0) {
            const entity = entities[0];
            viewer.trackedEntity = entity;
          }
        }
        console.log("added");
      }

      //if all the datasources loaded on map then zoom
      if (igcParsedDataCount > 0 && igcParsedDataCount == dataSource.length) {
        //zoom available data source
        for (let i = 0; i < dataSource.length; i++) {
          let ds = dataSource.get(i);
          if (!ds.show) continue;
          let entities = ds.entities.values;
          if (entities.length > 0) {
            const entity = entities[0];
            viewer.trackedEntity = entity;
            // zoomToEntityBound(entity, 2, false);
          }
        }
      }
    };

    cesiumEventHelper.add(
      viewer.dataSources.dataSourceAdded,
      cesiumDataSourceAddListener,
      cesiumEventHelper
    );
    cesiumEventHelper.add(
      viewer.dataSources.dataSourceRemoved,
      cesiumDataSourceRemoveListener,
      cesiumEventHelper
    );
  };

  return <></>;
};

export default CreateDataSource;
